import type { BriefEdit } from '@sigora/domain';
import { useCallback, useEffect, useRef, useState } from 'react';

import { type BriefReadView, editBrief, fetchBrief } from '../../api/client.ts';
import { domainCodeOf } from '../../api/errors.ts';
import { BRIEF_MOVED_CODES, BRIEF_UNPUBLISHED_CODE, type BriefEditOutcome } from './brief-edit.ts';

export type BriefReadState =
  { status: 'loading' } | { status: 'failed' } | { status: 'ready'; read: BriefReadView };

/**
 * A study's published Brief once it has been read, held by the study's shell so the Brief opens on
 * the last read when it is visited again: the shell is keyed by study, so nothing in it outlives the study or the
 * session it was read in.
 */
export interface BriefReadCache {
  current: BriefReadView | null;
}

/**
 * Word from the Brief's follow-ups that the Brief moved: the read a choice was answered with, to
 * draw as it is, or `null` when the move was seen without one and the Brief is to be read again.
 * `seq` counts the news, so the same read handed on twice is two pieces of news.
 */
export interface BriefNews {
  seq: number;
  read: BriefReadView | null;
}

const versionOf = (read: BriefReadView | null): number | null => read?.brief?.version ?? null;

/**
 * Whether `read` is an older snapshot of the Brief than `held`: a lower version, or the same
 * version with fewer finalized follow-ups. Outcomes are never taken back, so at one version the
 * count only grows, and it orders two reads however their answers arrived.
 */
const isBehind = (read: BriefReadView, held: BriefReadView | null): boolean => {
  const holding = held?.brief ?? null;
  const arriving = read.brief;
  if (holding === null || arriving === null) {
    return false;
  }
  return (
    arriving.version < holding.version ||
    (arriving.version === holding.version &&
      arriving.record.followUpCount < holding.record.followUpCount)
  );
};

/**
 * The study's Brief. A held read is drawn at once and the Brief is read again behind it on every
 * arrival, because a published Brief changes: the researcher edits it here or in another tab, and
 * Sigora revises it. Only a study with nothing held shows the loading line. `reload` is the read's
 * own Try again. A move of `refreshToken` — the study's conversation saying something may have
 * changed, such as a follow-up landing in the Brief — reads it again the same way, behind the page;
 * a move while an edit is out waits for that edit, and is read once it has landed unless the edit
 * read the Brief again itself. `news` from the follow-ups is the same signal with the answer often
 * in hand: a read a choice was answered with is taken through the gate below as if it had just
 * been read, with no request, unless it is older than the one drawn; news with none reads the
 * Brief again; either waits for an edit that is out, and then reads.
 *
 * Every read and every edit's answer goes through one gate. A read asked before a later one, or
 * before an edit was sent, is dropped when it lands; no answer replaces a Brief held at a higher
 * version, so an answer that arrives late never rolls the page back; and at the same version, an
 * answer asked for before the one drawn, or holding fewer finalized follow-ups, is dropped too,
 * since what the Brief's follow-ups added to it moves without a new version. What the gate takes is drawn and held in the shell's cache, so
 * the Brief opens again on itself as last read or edited.
 *
 * `edit` sends one framing edit over the version on screen and answers how it ended
 * (`BriefEditOutcome`). A refusal saying the Brief moved under it (`BRIEF_MOVED_CODES`) raises
 * `moved` until the next edit and reads the Brief again behind the page, so whatever the researcher
 * typed stays open; a Brief that is no longer published is read again with no notice, and the read
 * says so itself. An edit sent while a read was still out cancels that read, so an edit refused for
 * any other reason asks for it again behind the page. `saving` is true from an edit's send until its
 * answer, or the read after a moved one, has landed.
 *
 * A read that fails with nothing held is a state of its own, which says the Brief could not be
 * opened and offers Try again. One that fails behind a Brief on screen leaves that Brief, and any
 * open editor, where it is and raises `refreshFailed` instead, until a read lands.
 *
 * Everything held is the study's own. Given another study, the hook starts again from the loading
 * line with nothing held, and drops any answer still out for the study before.
 */
export const useBriefRead = (
  studyId: string,
  cache: BriefReadCache,
  refreshToken = 0,
  news: BriefNews | null = null,
): {
  state: BriefReadState;
  reload: () => void;
  edit: (change: BriefEdit) => Promise<BriefEditOutcome>;
  saving: boolean;
  moved: boolean;
  /** Raises the moved notice for a change the page found itself, outside an edit. */
  noteMoved: () => void;
  refreshFailed: boolean;
} => {
  const [state, setState] = useState<BriefReadState>(() =>
    cache.current === null ? { status: 'loading' } : { status: 'ready', read: cache.current },
  );
  const [attempt, setAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [moved, setMoved] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  // Another study's page owes nothing to an edit made on the study before.
  const [readFor, setReadFor] = useState(studyId);
  if (readFor !== studyId) {
    setReadFor(studyId);
    setSaving(false);
    setMoved(false);
    setRefreshFailed(false);
  }
  /** The read on screen, for the gate and the next edit's version, ahead of the next render. */
  const held = useRef<BriefReadView | null>(cache.current);
  /** The study whose Brief is held; an answer for any other is dropped. */
  const heldFor = useRef(studyId);
  /** Bumped by every read asked and every edit sent; an answer to an earlier one is dropped. */
  const latest = useRef(0);
  /** Which ask, by `latest`, the read on screen answered. */
  const heldAsked = useRef(0);
  /** The read still out, if any. */
  const inFlight = useRef<AbortController | null>(null);
  /** Whether an edit is out, so a refresh waits for it. */
  const editing = useRef(false);
  /** A refresh asked for while an edit was out, read once it lands. */
  const refreshOwed = useRef(false);

  const adopt = useCallback(
    (read: BriefReadView, asked: number) => {
      if (studyId !== heldFor.current) {
        return;
      }
      const holding = versionOf(held.current);
      const arriving = versionOf(read);
      if (
        isBehind(read, held.current) ||
        (holding !== null && arriving === holding && asked < heldAsked.current)
      ) {
        return;
      }

      held.current = read;
      heldAsked.current = asked;
      cache.current = read.brief === null ? null : read;
      setRefreshFailed(false);
      setState({ status: 'ready', read });
    },
    [studyId, cache],
  );

  /** Reads the Brief again, behind the page when one is drawn and through the loading line if not. */
  const read = useCallback(async () => {
    latest.current += 1;
    const token = latest.current;
    refreshOwed.current = false;
    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;
    if (held.current === null) {
      setState({ status: 'loading' });
    }

    try {
      const answer = await fetchBrief(studyId, controller.signal);
      if (token === latest.current) {
        adopt(answer, token);
      }
    } catch {
      if (token !== latest.current) {
        return;
      }
      if (held.current === null) {
        setState({ status: 'failed' });
      } else {
        setRefreshFailed(true);
      }
    } finally {
      if (inFlight.current === controller) {
        inFlight.current = null;
      }
    }
  }, [studyId, adopt]);

  // Ahead of the read below, so another study's first read starts from the loading line with
  // nothing held.
  useEffect(() => {
    if (heldFor.current !== studyId) {
      heldFor.current = studyId;
      held.current = null;
      heldAsked.current = 0;
      cache.current = null;
      editing.current = false;
      refreshOwed.current = false;
    }
  }, [studyId, cache]);

  useEffect(() => {
    void read();
  }, [read, attempt]);

  // A move of the token reads the Brief again, after any edit still out.
  const tokenRead = useRef(refreshToken);
  useEffect(() => {
    if (tokenRead.current === refreshToken) {
      return;
    }
    tokenRead.current = refreshToken;
    if (editing.current) {
      refreshOwed.current = true;
      return;
    }
    void read();
  }, [refreshToken, read]);

  // News from the follow-ups: its read is drawn through the gate with no request, and news with
  // none reads the Brief again, each after any edit still out. News from before this page was drawn
  // is not news to it.
  const newsTaken = useRef(news?.seq ?? 0);
  useEffect(() => {
    if (news === null || newsTaken.current === news.seq) {
      return;
    }
    newsTaken.current = news.seq;
    if (editing.current) {
      refreshOwed.current = true;
      return;
    }
    if (news.read === null) {
      void read();
      return;
    }
    // A snapshot older than the one drawn (two outcomes answered out of order) is not news, and
    // any read still out stays out.
    if (isBehind(news.read, held.current)) {
      return;
    }
    // Answered after any read still out was asked, so that read is dropped as one asked before it.
    latest.current += 1;
    inFlight.current?.abort();
    inFlight.current = null;
    adopt(news.read, latest.current);
  }, [news, read, adopt]);

  useEffect(() => () => inFlight.current?.abort(), []);

  const reload = useCallback(() => setAttempt((count) => count + 1), []);
  const noteMoved = useCallback(() => setMoved(true), []);

  const edit = useCallback(
    async (change: BriefEdit): Promise<BriefEditOutcome> => {
      const version = versionOf(held.current);
      if (version === null) {
        return { status: 'refused', failure: null };
      }

      // A read asked before this edit answers for the Brief before it, so it is not waited for.
      latest.current += 1;
      const asked = latest.current;
      const cancelledRead = inFlight.current !== null;
      inFlight.current?.abort();
      inFlight.current = null;
      editing.current = true;
      setSaving(true);
      setMoved(false);
      try {
        const { read: answer } = await editBrief({
          studyId,
          edit: change,
          expectedVersion: version,
        });
        adopt(answer, asked);
        return { status: 'saved' };
      } catch (failure) {
        // Answered after the page moved to another study: the controls that asked are gone, and
        // nothing about the study before is this page's to say or read.
        if (studyId !== heldFor.current) {
          return { status: 'refused', failure };
        }
        const code = domainCodeOf(failure);
        if (code === BRIEF_UNPUBLISHED_CODE) {
          await read();
          return { status: 'unpublished' };
        }
        if (code !== null && BRIEF_MOVED_CODES.has(code)) {
          setMoved(true);
          await read();
          return { status: 'moved' };
        }
        // The page may be behind the Brief that read would have brought; the refusal is said at once
        // and the read lands behind it.
        if (cancelledRead) {
          void read();
        }

        return { status: 'refused', failure };
      } finally {
        // An edit answered for the study before holds nothing on this one, which may have its own
        // edit out.
        if (studyId === heldFor.current) {
          setSaving(false);
          editing.current = false;
          if (refreshOwed.current) {
            void read();
          }
        }
      }
    },
    [studyId, adopt, read],
  );

  return { state, reload, edit, saving, moved, noteMoved, refreshFailed };
};
